import { useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";

// Abstract, architectural growth visual — SVG only, no fake financial data.
export default function GrowthChart() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ x: px * 10, y: py * 10 });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  // ascending bars (no values shown)
  const bars = [
    { x: 60, h: 60 },
    { x: 120, h: 92 },
    { x: 180, h: 78 },
    { x: 240, h: 130 },
    { x: 300, h: 116 },
    { x: 360, h: 176 },
    { x: 420, h: 158 },
  ];
  const baseY = 300;
  const barW = 26;

  const curve =
    "M 60 262 C 120 250, 150 214, 200 208 C 260 200, 300 150, 360 132 C 400 120, 430 96, 456 74";

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative w-full"
      data-testid="hero-growth-chart"
      aria-hidden="true"
    >
      {/* soft gold ambience */}
      <div className="pointer-events-none absolute right-6 top-6 h-40 w-40 rounded-full bg-fs-gold/10 blur-3xl" />

      <motion.svg
        viewBox="0 0 500 340"
        className="relative w-full"
        style={{ overflow: "visible" }}
        animate={reduce ? {} : { rotateX: -tilt.y, rotateY: tilt.x }}
        transition={{ type: "spring", stiffness: 80, damping: 18 }}
      >
        {/* fine grid */}
        <g stroke="rgba(255,255,255,0.06)" strokeWidth="1">
          {[60, 120, 180, 240, 300].map((y) => (
            <line key={`h${y}`} x1="20" y1={y} x2="480" y2={y} />
          ))}
          {[80, 160, 240, 320, 400].map((x) => (
            <line key={`v${x}`} x1={x} y1="40" x2={x} y2="300" />
          ))}
        </g>

        {/* baseline */}
        <line x1="20" y1={baseY} x2="480" y2={baseY} stroke="rgba(255,255,255,0.18)" strokeWidth="1" />

        {/* bars */}
        {bars.map((b, i) => (
          <motion.rect
            key={i}
            x={b.x - barW / 2}
            width={barW}
            rx="1"
            fill="rgba(197,162,74,0.10)"
            stroke="rgba(197,162,74,0.28)"
            strokeWidth="1"
            initial={reduce ? { height: b.h, y: baseY - b.h, opacity: 0.6 } : { height: 0, y: baseY, opacity: 0 }}
            whileInView={{ height: b.h, y: baseY - b.h, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
          />
        ))}

        {/* growth curve, drawn progressively */}
        <motion.path
          d={curve}
          fill="none"
          stroke="#C5A24A"
          strokeWidth="2.5"
          strokeLinecap="round"
          initial={reduce ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0.9 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, delay: 0.5, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 0 6px rgba(197,162,74,0.35))" }}
        />

        {/* illuminated endpoint */}
        <motion.circle
          cx="456"
          cy="74"
          r="6"
          fill="#C5A24A"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 2.0, duration: 0.5 }}
          style={{ filter: "drop-shadow(0 0 12px rgba(197,162,74,0.9))" }}
        />
        <motion.circle
          cx="456"
          cy="74"
          r="14"
          fill="none"
          stroke="#C5A24A"
          strokeWidth="1"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.5 }}
          viewport={{ once: true }}
          transition={{ delay: 2.1, duration: 0.6 }}
        />
      </motion.svg>
    </div>
  );
}
